import { Box, Chip, Divider, Stack, Typography } from '@mui/material';

// Quill (react-quill) stores "Steps to Reproduce" as rich-text HTML. Findings on this
// platform are pentest write-ups, so the raw HTML can legitimately contain attacker-authored
// markup (e.g. a participant pasting an XSS payload as part of their PoC) — rendering it with
// dangerouslySetInnerHTML unfiltered would let a participant's submission execute script in
// whoever's browser is viewing it. Strip to a small allowlist of formatting tags and drop all
// attributes first.
const ALLOWED_HTML_TAGS = new Set(['P', 'BR', 'STRONG', 'EM', 'B', 'I', 'OL', 'UL', 'LI', 'H1', 'H2', 'PRE', 'CODE', 'SPAN', 'BLOCKQUOTE']);

export function sanitizeQuillHtml(html) {
  if (!html) return '';
  const doc = new DOMParser().parseFromString(html, 'text/html');
  const strip = (node) => {
    Array.from(node.childNodes).forEach(child => {
      if (child.nodeType !== Node.ELEMENT_NODE) return;
      if (!ALLOWED_HTML_TAGS.has(child.tagName)) {
        child.replaceWith(child.ownerDocument.createTextNode(child.textContent));
        return;
      }
      Array.from(child.attributes).forEach(attr => child.removeAttribute(attr.name));
      strip(child);
    });
  };
  strip(doc.body);
  return doc.body.innerHTML;
}

// Read-only rendering of one finding's fields — shared by the admin Participant Dossier
// and the participant-facing Findings Summary page, so both stay visually consistent.
export function FindingFields({ finding }) {
  return (
    <Stack spacing={2}>
      {(finding.vulnerability_type || []).length > 0 && (
        <Stack direction="row" flexWrap="wrap" gap={0.5}>
          {finding.vulnerability_type.map(vt => (
            <Chip key={vt} size="small" color="primary" label={vt} />
          ))}
        </Stack>
      )}

      {finding.affected_urls && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Affected Endpoint</Typography>
          <Typography variant="body2" sx={{ fontFamily: 'monospace', wordBreak: 'break-word' }}>
            {finding.affected_urls}
          </Typography>
        </Box>
      )}

      {finding.description && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Description</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{finding.description}</Typography>
        </Box>
      )}

      {finding.payload && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Steps to Reproduce</Typography>
          <Box
            sx={{ maxHeight: 320, overflow: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 1.5 }}
            dangerouslySetInnerHTML={{ __html: sanitizeQuillHtml(finding.payload) }}
          />
        </Box>
      )}

      {finding.impact && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">Impact</Typography>
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>{finding.impact}</Typography>
        </Box>
      )}

      {finding.poc_image_path && (
        <Box>
          <Typography variant="caption" color="text.secondary" display="block">PoC Screenshot</Typography>
          <a href={finding.poc_image_path} target="_blank" rel="noopener noreferrer">
            <Box component="img" src={finding.poc_image_path} alt="PoC" sx={{ maxWidth: 320, borderRadius: 1, border: '1px solid', borderColor: 'divider' }} />
          </a>
        </Box>
      )}

      <Divider />

      <Stack direction={{ xs: 'column', sm: 'row' }} spacing={4}>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Used Threat Model?</Typography>
            <Chip size="small" label={finding.used_threat_model ? 'Yes' : 'No'} color={finding.used_threat_model ? 'success' : 'default'} variant="outlined" />
          </Stack>
          {(finding.threat_model_artifacts || []).length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              {finding.threat_model_artifacts.map(a => (
                <Chip key={a} size="small" variant="outlined" label={a} />
              ))}
            </Stack>
          )}
          {finding.threat_model_info && (
            <Typography variant="body2" color="text.secondary" fontStyle="italic" mt={0.5}>{finding.threat_model_info}</Typography>
          )}
        </Box>
        <Box>
          <Stack direction="row" spacing={1} alignItems="center">
            <Typography variant="caption" color="text.secondary">Used Design Document?</Typography>
            <Chip size="small" label={finding.used_design_document ? 'Yes' : 'No'} color={finding.used_design_document ? 'success' : 'default'} variant="outlined" />
          </Stack>
          {(finding.design_document_artifacts || []).length > 0 && (
            <Stack direction="row" flexWrap="wrap" gap={0.5} mt={0.5}>
              {finding.design_document_artifacts.map(a => (
                <Chip key={a} size="small" variant="outlined" label={a} />
              ))}
            </Stack>
          )}
          {finding.design_document_info && (
            <Typography variant="body2" color="text.secondary" fontStyle="italic" mt={0.5}>{finding.design_document_info}</Typography>
          )}
        </Box>
      </Stack>
    </Stack>
  );
}
